import {
    Alert,
    Button,
    Card,
    Col,
    DatePicker,
    Descriptions,
    Form,
    Image,
    Input,
    InputNumber,
    Modal,
    Progress,
    Row,
    Select,
    Space,
    Statistic,
    Table,
    Tag,
    Tooltip,
    Upload,
    message,
} from 'antd';
import { Helmet } from 'react-helmet-async';
import { useEffect, useMemo, useState } from 'react';
import {
    DeleteOutlined,
    EditOutlined,
    EyeOutlined,
    PlusOutlined,
    DownloadOutlined,
    UploadOutlined,
    FileExcelOutlined,
} from '@ant-design/icons';
import { apiCall } from '../../API/RestApi';
import { modalError } from '../../Component/Modal/ModalLayout';
import { useSelector } from 'react-redux';
import Search from 'antd/es/input/Search';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';

function Asset() {
    const token = useSelector((state) => state.user.token);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterCondition, setFilterCondition] = useState('');

    const [isModal, setIsModal] = useState(false);
    const [dataModal, setDataModal] = useState(null);

    const [isDetailModal, setIsDetailModal] = useState(false);
    const [detailData, setDetailData] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);

    const [categoryOptions, setCategoryOptions] = useState([]);
    const [areaOptions, setAreaOptions] = useState([]);

    const [isImportModal, setIsImportModal] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [loadingImport, setLoadingImport] = useState(false);
    const [loadingExport, setLoadingExport] = useState(false);
    const [vendorOptions, setVendorOptions] = useState([]);
    const [assetImageFiles, setAssetImageFiles] = useState([]);
    const [existingImages, setExistingImages] = useState([]);

    const [analyticSummary, setAnalyticSummary] = useState({
        total_assets: 0,
        active_assets: 0,
        inactive_assets: 0,
        disposed_assets: 0,
        available_assets: 0,
    });
    const [analyticStatus, setAnalyticStatus] = useState([]);
    const [analyticCondition, setAnalyticCondition] = useState([]);
    const [analyticCategory, setAnalyticCategory] = useState([]);
    const [loadingAnalytics, setLoadingAnalytics] = useState(false);

    const [form] = Form.useForm();

    const [page, setPage] = useState({
        pagination: {
            current: 1,
            pageSize: 10,
            total: 0,
        },
    });

    const [isAlert, setIsAlert] = useState(false);
    const [textAlert, setTextAlert] = useState('');

    const titlePage = 'Asset Data';

    const assetStatusOptions = [
        { value: 1, label: 'Active' },
        { value: 2, label: 'Pending' },
        { value: 3, label: 'Assigned' },
        { value: 4, label: 'Inactive' },
        { value: 5, label: 'Disposed' },
        { value: 6, label: 'Maintenance' },
        { value: 7, label: 'Rejected' },
    ];

    const conditionStatusOptions = [
        { value: 'Good', label: 'Good' },
        { value: 'Minor Damage', label: 'Minor Damage' },
        { value: 'Broken', label: 'Broken' },
    ];

    useEffect(() => {
        loadAssets();
    }, [JSON.stringify(page.pagination), search, filterStatus, filterCondition]);

    useEffect(() => {
        loadAreas();
        loadAssetCategories();
        loadVendors();
        loadAnalytics();
    }, []);

    const currencyFormat = (value) => Number(value || 0).toLocaleString('id-ID');

    const getHeaders = () => ({
        Authorization: 'Bearer ' + token,
    });

    const getAssetStatusNameById = (id) => {
        return assetStatusOptions.find((item) => Number(item.value) === Number(id))?.label || '-';
    };

    const getAssetStatusName = (record) => {
        return record?.status?.name || record?.status_name || getAssetStatusNameById(record?.asset_status_id);
    };

    const percentSummary = useMemo(() => {
        const total = Number(analyticSummary?.total_assets || 0);

        if (!total) {
            return {
                active: 0,
                inactive: 0,
                disposed: 0,
                available: 0,
            };
        }

        return {
            active: Math.round((Number(analyticSummary?.active_assets || 0) / total) * 100),
            inactive: Math.round((Number(analyticSummary?.inactive_assets || 0) / total) * 100),
            disposed: Math.round((Number(analyticSummary?.disposed_assets || 0) / total) * 100),
            available: Math.round((Number(analyticSummary?.available_assets || 0) / total) * 100),
        };
    }, [analyticSummary]);

    const renderAssetStatus = (status) => {
        if (status === 'Active') return <Tag color="green">Available</Tag>;
        if (status === 'Pending') return <Tag color="gold">{status}</Tag>;
        if (status === 'Assigned') return <Tag color="blue">{status}</Tag>;
        if (status === 'Inactive') return <Tag color="orange">{status}</Tag>;
        if (status === 'Disposed') return <Tag color="red">{status}</Tag>;
        if (status === 'Maintenance') return <Tag color="purple">{status}</Tag>;
        if (status === 'Rejected') return <Tag color="volcano">{status}</Tag>;
        return <Tag>{status || '-'}</Tag>;
    };

    const renderConditionStatus = (status) => {
        if (status === 'Good') return <Tag color="blue">{status}</Tag>;
        if (status === 'Minor Damage') return <Tag color="orange">{status}</Tag>;
        if (status === 'Broken') return <Tag color="red">{status}</Tag>;
        return <Tag>{status || '-'}</Tag>;
    };

    const columns = [
        {
            title: 'No',
            key: 'no',
            width: 70,
            render: (_, record, index) =>
                (page.pagination.current - 1) * page.pagination.pageSize + index + 1,
        },
        {
            title: 'Asset Code',
            dataIndex: 'asset_code',
            key: 'asset_code',
            width: 180,
            render: (text) => text || '-',
        },
        {
            title: 'Category',
            key: 'category',
            width: 160,
            render: (_, record) => record?.category?.name || '-',
        },
        {
            title: 'Area',
            dataIndex: 'area',
            key: 'area',
            width: 180,
            render: (_, record) => record?.area?.name || '-',
        },
        {
            title: 'Serial Number',
            dataIndex: 'serial_number',
            key: 'serial_number',
            width: 150,
            render: (text) => text || '-',
        },
        {
            title: 'Brand',
            dataIndex: 'brand',
            key: 'brand',
            width: 120,
            render: (text) => text || '-',
        },
        {
            title: 'Purchase Price',
            dataIndex: 'purchase_price',
            key: 'purchase_price',
            width: 150,
            render: (text) => currencyFormat(text),
        },
        {
            title: 'Condition',
            dataIndex: 'condition_status',
            key: 'condition_status',
            width: 140,
            render: (text) => renderConditionStatus(text),
        },
        {
            title: 'Status',
            dataIndex: 'asset_status_id',
            key: 'asset_status_id',
            width: 140,
            render: (_, record) => renderAssetStatus(getAssetStatusName(record)),
        },
        {
            title: 'Action',
            key: 'action',
            fixed: 'right',
            width: 190,
            render: (_, record) => (
                <Space>
                    <Tooltip title="Detail">
                        <Button onClick={() => openDetailModal(record)}>
                            <EyeOutlined />
                        </Button>
                    </Tooltip>

                    <Tooltip title="Edit">
                        <Button type="primary" onClick={() => openModal('edit', record)}>
                            <EditOutlined />
                        </Button>
                    </Tooltip>

                    <Tooltip title="Delete">
                        <Button type="primary" danger onClick={() => modalDeleteData(record)}>
                            <DeleteOutlined />
                        </Button>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const analyticStatusColumns = [
        {
            title: 'Status',
            dataIndex: 'name',
            key: 'name',
            render: (value) => renderAssetStatus(value),
        },
        {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            width: 120,
        },
    ];

    const analyticConditionColumns = [
        {
            title: 'Condition',
            dataIndex: 'name',
            key: 'name',
            render: (value) => renderConditionStatus(value),
        },
        {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            width: 120,
        },
    ];

    const analyticCategoryColumns = [
        {
            title: 'Category',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            width: 120,
        },
    ];

    const loadAssets = () => {
        const params = new URLSearchParams({
            search: search || '',
            page: page.pagination.current,
            limit: page.pagination.pageSize,
        });

        if (filterStatus) params.append('asset_status_id', filterStatus);
        if (filterCondition) params.append('condition_status', filterCondition);

        getApi('GET', `/assets?${params.toString()}`);
    };

    const loadVendors = () => {
        apiCall({
            method: 'GET',
            url: '/vendors?nopage=true',
            data: {
                headers: getHeaders(),
            },
        })
            .then((res) => {
                if (res.status === 'success') {
                    const list = Array.isArray(res?.data?.data)
                        ? res.data.data
                        : [];

                    setVendorOptions(
                        list.map((item) => ({
                            value: item.vendor_id,
                            label: item.vendor_name,
                        }))
                    );
                }
            })
            .catch((e) => console.log(e.message));
    };

    const getApi = (method, url) => {
        setLoading(true);

        apiCall({
            method,
            url,
            data: {
                headers: getHeaders(),
            },
        })
            .then((res) => {
                if (res.status === 'success') {
                    setData(res.data.data || []);
                    setPage((prev) => ({
                        ...prev,
                        pagination: {
                            ...prev.pagination,
                            total: res.data.total_data || 0,
                        },
                    }));
                } else {
                    modalError(res.message || 'Gagal mengambil data');
                }
            })
            .catch((e) => modalError(e.message))
            .finally(() => setLoading(false));
    };

    const loadAnalytics = async () => {
        setLoadingAnalytics(true);

        try {
            const [summaryRes, statusRes, conditionRes, categoryRes] = await Promise.all([
                apiCall({
                    method: 'GET',
                    url: '/asset-analytics/summary',
                    data: { headers: getHeaders() },
                }),
                apiCall({
                    method: 'GET',
                    url: '/asset-analytics/status',
                    data: { headers: getHeaders() },
                }),
                apiCall({
                    method: 'GET',
                    url: '/asset-analytics/condition',
                    data: { headers: getHeaders() },
                }),
                apiCall({
                    method: 'GET',
                    url: '/asset-analytics/category',
                    data: { headers: getHeaders() },
                }),
            ]);

            if (summaryRes?.status === 'success') setAnalyticSummary(summaryRes?.data || {});
            if (statusRes?.status === 'success') setAnalyticStatus(statusRes?.data || []);
            if (conditionRes?.status === 'success') setAnalyticCondition(conditionRes?.data || []);
            if (categoryRes?.status === 'success') setAnalyticCategory(categoryRes?.data || []);
        } catch (e) {
            console.log(e.message);
        } finally {
            setLoadingAnalytics(false);
        }
    };

    const loadAssetCategories = () => {
        apiCall({
            method: 'GET',
            url: '/asset-categories?nopage=true',
            data: {
                headers: getHeaders(),
            },
        })
            .then((res) => {
                if (res.status === 'success') {
                    const list = Array.isArray(res?.data?.data)
                        ? res.data.data
                        : Array.isArray(res?.data)
                            ? res.data
                            : [];

                    setCategoryOptions(
                        list.map((item) => ({
                            value: item.asset_category_id,
                            label: item.name || `Category #${item.asset_category_id}`,
                        }))
                    );
                }
            })
            .catch((e) => console.log(e.message));
    };

    const loadAreas = () => {
        apiCall({
            method: 'GET',
            url: '/area?nopage=true',
            data: {
                headers: getHeaders(),
            },
        })
            .then((res) => {
                if (res.status === 'success') {
                    const list = Array.isArray(res?.data?.data)
                        ? res.data.data
                        : Array.isArray(res?.data)
                            ? res.data
                            : [];

                    setAreaOptions(
                        list.map((item) => ({
                            value: item.area_id,
                            label: item.name || `Area #${item.area_id}`,
                        }))
                    );
                }
            })
            .catch((e) => console.log(e.message));
    };

    const getDetailApi = (id) => {
        setLoadingDetail(true);

        apiCall({
            method: 'GET',
            url: `/assets/${id}`,
            data: {
                headers: getHeaders(),
            },
        })
            .then((res) => {
                if (res.status === 'success') {
                    setDetailData(res.data);
                    setIsDetailModal(true);
                } else {
                    modalError(res.message || 'Gagal mengambil detail');
                }
            })
            .catch((e) => modalError(e.message))
            .finally(() => setLoadingDetail(false));
    };

    const modalDeleteData = (record) => {
        Modal.confirm({
            title: 'NOTICE',
            content: `Do you want to delete asset "${record.asset_name}"?`,
            onOk: async () => {
                setLoading(true);

                try {
                    const res = await apiCall({
                        method: 'DELETE',
                        url: '/assets/' + record.asset_id,
                        data: {
                            headers: getHeaders(),
                        },
                    });

                    if (res.status === 'success') {
                        setIsAlert(true);
                        setTextAlert(res.message);
                        loadAssets();
                        loadAnalytics();
                    } else {
                        modalError(res.message || 'Gagal menghapus data');
                    }
                } catch (e) {
                    modalError(e.message);
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const openModal = (type, data) => {
        if (type === 'edit' && data) {
            setDataModal(data);
            setExistingImages(data?.images || []);
            setAssetImageFiles([]);

            form.setFieldsValue({
                asset_category_id: data.asset_category_id,
                area_id: data.area_id,
                asset_name: data.asset_name,
                serial_number: data.serial_number,
                brand: data.brand,
                model: data.model,
                purchase_date: data.purchase_date ? dayjs(data.purchase_date) : null,
                purchase_price: data.purchase_price,
                location: data.location,
                condition_status: data.condition_status,
                asset_status_id: data.asset_status_id,
                notes: data.notes,
                vendor_id: data.vendor_id,
            });
        } else {
            setDataModal(null);
            setExistingImages([]);
            setAssetImageFiles([]);
            form.resetFields();
            form.setFieldsValue({
                asset_status_id: 1,
                condition_status: 'Good',
            });
        }

        setIsModal(true);
    };

    const openDetailModal = (record) => {
        getDetailApi(record.asset_id);
    };

    const onFinish = async (values) => {
        setLoading(true);

        const formData = new FormData();

        formData.append('asset_category_id', values.asset_category_id);
        formData.append('area_id', values.area_id);
        formData.append('asset_name', values.asset_name || '');
        formData.append('serial_number', values.serial_number || '');
        formData.append('brand', values.brand || '');
        formData.append('model', values.model || '');
        formData.append('vendor_id', values.vendor_id || '');
        formData.append(
            'purchase_date',
            values.purchase_date ? dayjs(values.purchase_date).format('YYYY-MM-DD') : ''
        );
        formData.append('purchase_price', values.purchase_price || 0);
        formData.append('location', values.location || '');
        formData.append('condition_status', values.condition_status || 'Good');
        formData.append('asset_status_id', values.asset_status_id || 1);
        formData.append('notes', values.notes || '');

        if (assetImageFiles?.length > 0) {
            assetImageFiles.forEach((file) => {
                formData.append('images', file);
            });
        }

        try {
            const res = await apiCall({
                method: dataModal ? 'PUT' : 'POST',
                url: dataModal ? `/assets/${dataModal.asset_id}` : `/assets`,
                data: {
                    data: formData,
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            });

            if (res.status !== 'success') {
                modalError(res.message || 'Gagal menyimpan data');
                return;
            }

            setDataModal(null);
            setIsModal(false);
            setIsAlert(true);
            setTextAlert(res.message || 'Berhasil');
            form.resetFields();
            setAssetImageFiles([]);
            setExistingImages([]);
            loadAssets();
            loadAnalytics();
        } catch (err) {
            console.error(err);
            modalError('Gagal menyimpan data');
        } finally {
            setLoading(false);
        }
    };

    const closeModal = () => {
        setDataModal(null);
        setIsModal(false);
        setExistingImages([]);
        setAssetImageFiles([]);
        form.resetFields();
    };

    const closeDetailModal = () => {
        setIsDetailModal(false);
        setDetailData(null);
    };

    const handleTableChange = (pagination) => {
        setPage({ pagination });
    };

    const searchData = (value) => {
        setSearch(value);
        setPage((prev) => ({
            ...prev,
            pagination: {
                ...prev.pagination,
                current: 1,
            },
        }));
    };

    const resetFilter = () => {
        setSearch('');
        setFilterStatus('');
        setFilterCondition('');
        setPage({
            pagination: {
                current: 1,
                pageSize: 10,
                total: 0,
            },
        });
    };

    const handleDownloadTemplate = () => {
        window.open('/assets/excel/TEMPLATE_ASSET.xlsx', '_blank');
    };

    const handleExportExcel = async () => {
        try {
            setLoadingExport(true);

            const params = new URLSearchParams({
                search: search || '',
                nopage: '1',
            });

            if (filterStatus) params.append('asset_status_id', filterStatus);
            if (filterCondition) params.append('condition_status', filterCondition);

            const res = await apiCall({
                method: 'GET',
                url: `/assets?${params.toString()}`,
                data: {
                    headers: getHeaders(),
                },
            });

            if (res.status !== 'success') {
                message.error(res.message || 'Gagal export data');
                return;
            }

            const list = res?.data?.data || [];

            if (!list.length) {
                message.warning('Tidak ada data untuk diexport');
                return;
            }

            const exportData = list.map((item, index) => ({
                No: index + 1,
                'Asset Code': item.asset_code || '-',
                'Asset Category': item?.category?.name || '-',
                // 'Asset Name': item.asset_name || '-',
                'Serial Number': item.serial_number || '-',
                Vendor: item?.vendor?.vendor_name || '-',
                Brand: item.brand || '-',
                Model: item.model || '-',
                'Purchase Date': item.purchase_date
                    ? dayjs(item.purchase_date).format('YYYY-MM-DD')
                    : '-',
                'Purchase Price': item.purchase_price || 0,
                Location: item.location || '-',
                'Condition Status': item.condition_status || '-',
                'Asset Status': item?.status?.name || getAssetStatusNameById(item.asset_status_id),
                Notes: item.notes || '-',
            }));

            const worksheet = XLSX.utils.json_to_sheet(exportData, { origin: 0 });
            const headerKeys = Object.keys(exportData[0]);

            headerKeys.forEach((key, idx) => {
                const cellRef = XLSX.utils.encode_cell({ r: 0, c: idx });
                if (!worksheet[cellRef]) return;

                worksheet[cellRef].s = {
                    font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 12 },
                    fill: { fgColor: { rgb: '1F4E78' } },
                    alignment: { horizontal: 'center', vertical: 'center' },
                    border: {
                        top: { style: 'thin', color: { rgb: '000000' } },
                        bottom: { style: 'thin', color: { rgb: '000000' } },
                        left: { style: 'thin', color: { rgb: '000000' } },
                        right: { style: 'thin', color: { rgb: '000000' } },
                    },
                };
            });

            const range = XLSX.utils.decode_range(worksheet['!ref']);

            for (let R = 1; R <= range.e.r; ++R) {
                for (let C = 0; C <= range.e.c; ++C) {
                    const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
                    if (!worksheet[cellRef]) continue;

                    worksheet[cellRef].s = {
                        border: {
                            top: { style: 'thin', color: { rgb: 'CCCCCC' } },
                            bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
                            left: { style: 'thin', color: { rgb: 'CCCCCC' } },
                            right: { style: 'thin', color: { rgb: 'CCCCCC' } },
                        },
                        alignment: {
                            vertical: 'center',
                            horizontal: C === 0 ? 'center' : 'left',
                        },
                    };
                }
            }

            worksheet['!cols'] = headerKeys.map((header) => {
                const maxHeaderLength = header.length;
                const maxDataLength = exportData.reduce((max, row) => {
                    const cellValue = row[header] ? String(row[header]) : '';
                    return cellValue.length > max ? cellValue.length : max;
                }, 0);

                return { wch: Math.max(maxHeaderLength, maxDataLength) + 2 };
            });

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Asset');

            const excelBuffer = XLSX.write(workbook, {
                bookType: 'xlsx',
                type: 'array',
            });

            const dataBlob = new Blob([excelBuffer], {
                type: 'application/octet-stream',
            });

            saveAs(dataBlob, 'asset_data.xlsx');
            message.success('Export Excel berhasil');
        } catch (e) {
            message.error(e.message || 'Gagal export Excel');
        } finally {
            setLoadingExport(false);
        }
    };

    const handleOpenImportModal = () => {
        setImportFile(null);
        setIsImportModal(true);
    };

    const handleCloseImportModal = () => {
        setImportFile(null);
        setIsImportModal(false);
    };

    const uploadProps = {
        beforeUpload: (file) => {
            const fileName = file.name.toLowerCase();

            const isExcel =
                file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                file.type === 'application/vnd.ms-excel' ||
                fileName.endsWith('.xlsx') ||
                fileName.endsWith('.xls');

            if (!isExcel) {
                message.error('File harus format .xlsx atau .xls');
                return Upload.LIST_IGNORE;
            }

            setImportFile(file);
            return false;
        },
        onRemove: () => {
            setImportFile(null);
        },
        maxCount: 1,
        fileList: importFile ? [importFile] : [],
    };

    const imageUploadProps = {
        beforeUpload: (file) => {
            const isImage = file.type?.startsWith('image/');
            if (!isImage) {
                message.error('File harus berupa gambar');
                return Upload.LIST_IGNORE;
            }

            setAssetImageFiles((prev) => [...prev, file]);
            return false;
        },
        onRemove: (file) => {
            setAssetImageFiles((prev) => prev.filter((item) => item.uid !== file.uid));
        },
        multiple: true,
        fileList: assetImageFiles,
    };

    const handleImportExcel = async () => {
        if (!importFile) {
            message.warning('Pilih file Excel terlebih dahulu');
            return;
        }

        try {
            setLoadingImport(true);

            const formData = new FormData();
            formData.append('file', importFile);

            const res = await apiCall({
                method: 'POST',
                url: '/assets/import',
                data: {
                    data: formData,
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            });

            if (res.status !== 'success') {
                message.error(res.message || 'Import gagal');
                return;
            }

            const result = res?.data || {};

            message.success(
                `Import selesai. Berhasil: ${result.success || 0}, Gagal: ${result.failed || 0}`
            );

            if (result.errors?.length > 0) {
                Modal.error({
                    title: 'Sebagian data gagal diimport',
                    width: 700,
                    content: (
                        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                            {result.errors.map((item, index) => (
                                <div key={index} style={{ marginBottom: 8 }}>
                                    <b>Row {item.row}</b>: {item.error}
                                </div>
                            ))}
                        </div>
                    ),
                });
            }

            setImportFile(null);
            setIsImportModal(false);
            loadAssets();
            loadAnalytics();
        } catch (e) {
            message.error(
                e?.response?.data?.message ||
                e?.message ||
                'Gagal import Excel'
            );
        } finally {
            setLoadingImport(false);
        }
    };

    return (
        <div>
            <Helmet>
                <title>{titlePage}</title>
            </Helmet>

            {isAlert && (
                <Alert
                    message={textAlert}
                    type="success"
                    banner
                    closable
                    afterClose={() => setIsAlert(false)}
                    style={{ marginBottom: 16 }}
                />
            )}

            <h3>Asset Data</h3>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col span={24}>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={8} lg={4}>
                            <Card loading={loadingAnalytics}>
                                <Statistic title="Total Assets" value={analyticSummary?.total_assets || 0} />
                            </Card>
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={5}>
                            <Card loading={loadingAnalytics}>
                                <Statistic title="Active" value={analyticSummary?.active_assets || 0} />
                                <Progress percent={percentSummary.active} size="small" showInfo={false} />
                            </Card>
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={5}>
                            <Card loading={loadingAnalytics}>
                                <Statistic title="Inactive" value={analyticSummary?.inactive_assets || 0} />
                                <Progress percent={percentSummary.inactive} size="small" showInfo={false} />
                            </Card>
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={5}>
                            <Card loading={loadingAnalytics}>
                                <Statistic title="Disposed" value={analyticSummary?.disposed_assets || 0} />
                                <Progress percent={percentSummary.disposed} size="small" showInfo={false} />
                            </Card>
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={5}>
                            <Card loading={loadingAnalytics}>
                                <Statistic title="Available" value={analyticSummary?.available_assets || 0} />
                                <Progress percent={percentSummary.available} size="small" showInfo={false} />
                            </Card>
                        </Col>
                    </Row>
                </Col>

                <Col xs={24} lg={8}>
                    <Card title="By Status" loading={loadingAnalytics}>
                        <Table
                            size="small"
                            columns={analyticStatusColumns}
                            dataSource={analyticStatus}
                            rowKey={(record, index) => `${record.name}-${index}`}
                            pagination={false}
                        />
                    </Card>
                </Col>

                <Col xs={24} lg={8}>
                    <Card title="By Condition" loading={loadingAnalytics}>
                        <Table
                            size="small"
                            columns={analyticConditionColumns}
                            dataSource={analyticCondition}
                            rowKey={(record, index) => `${record.name}-${index}`}
                            pagination={false}
                        />
                    </Card>
                </Col>

                <Col xs={24} lg={8}>
                    <Card title="Top Categories" loading={loadingAnalytics}>
                        <Table
                            size="small"
                            columns={analyticCategoryColumns}
                            dataSource={analyticCategory.slice(0, 5)}
                            rowKey={(record, index) => `${record.asset_category_id || index}`}
                            pagination={false}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={8}>
                    <Search
                        placeholder="Search code, brand, model..."
                        allowClear
                        enterButton="Search"
                        size="middle"
                        onSearch={searchData}
                    />
                </Col>

                <Col span={4}>
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Filter Asset Status"
                        allowClear
                        value={filterStatus || undefined}
                        options={assetStatusOptions}
                        onChange={(value) => {
                            setFilterStatus(value || '');
                            setPage((prev) => ({
                                ...prev,
                                pagination: {
                                    ...prev.pagination,
                                    current: 1,
                                },
                            }));
                        }}
                    />
                </Col>

                <Col span={4}>
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Filter Condition"
                        allowClear
                        value={filterCondition || undefined}
                        options={conditionStatusOptions}
                        onChange={(value) => {
                            setFilterCondition(value || '');
                            setPage((prev) => ({
                                ...prev,
                                pagination: {
                                    ...prev.pagination,
                                    current: 1,
                                },
                            }));
                        }}
                    />
                </Col>

                <Col
                    span={8}
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 8,
                        flexWrap: 'wrap',
                    }}
                >
                    <Button onClick={resetFilter}>Refresh</Button>

                    <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
                        Template
                    </Button>

                    <Button icon={<UploadOutlined />} onClick={handleOpenImportModal}>
                        Import
                    </Button>

                    <Button icon={<FileExcelOutlined />} onClick={handleExportExcel} loading={loadingExport}>
                        Export
                    </Button>

                    <Button type="primary" onClick={() => openModal('add', null)}>
                        <PlusOutlined />
                        <b style={{ marginLeft: 5 }}>Add</b>
                    </Button>
                </Col>
            </Row>

            <Table
                size="middle"
                columns={columns}
                dataSource={data}
                pagination={page.pagination}
                loading={loading}
                onChange={handleTableChange}
                rowKey={(record) => record.asset_id}
                scroll={{ x: 1900 }}
            />

            <Modal
                title={dataModal ? 'Edit Asset' : 'Add Asset'}
                open={isModal}
                onOk={form.submit}
                onCancel={closeModal}
                okText="Save"
                confirmLoading={loading}
                width={900}
            >
                <Form form={form} layout="vertical" onFinish={onFinish}>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="asset_category_id"
                                label="Asset Category"
                                rules={[{ required: true, message: 'Asset Category is required!' }]}
                            >
                                <Select showSearch placeholder="Select asset category" options={categoryOptions} optionFilterProp="label" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="vendor_id"
                                label="Vendor"
                            >
                                <Select
                                    showSearch
                                    placeholder="Select vendor"
                                    options={vendorOptions}
                                    optionFilterProp="label"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="area_id"
                                label="Area"
                                rules={[{ required: true, message: 'Area is required!' }]}
                            >
                                <Select
                                    showSearch
                                    placeholder="Select area"
                                    options={areaOptions}
                                    optionFilterProp="label"
                                />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item name="serial_number" label="Serial Number">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="brand" label="Brand">
                                <Input />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item name="model" label="Model">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="purchase_date" label="Purchase Date">
                                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item name="purchase_price" label="Purchase Price">
                                <InputNumber
                                    min={0}
                                    style={{ width: '100%' }}
                                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={(value) => value?.replace(/\$\s?|(,*)/g, '')}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name="location" label="Location">
                                <Input.TextArea rows={3} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="condition_status"
                                label="Condition Status"
                                rules={[{ required: true, message: 'Condition Status is required!' }]}
                            >
                                <Select options={conditionStatusOptions} />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                name="asset_status_id"
                                label="Asset Status"
                                rules={[{ required: true, message: 'Asset Status is required!' }]}
                            >
                                <Select
                                    options={assetStatusOptions.filter(
                                        (item) => item.label !== 'Rejected'
                                    )} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="notes" label="Notes">
                        <Input.TextArea rows={4} />
                    </Form.Item>

                    {dataModal && existingImages?.length > 0 && (
                        <Form.Item label="Current Images">
                            <Space wrap>
                                {existingImages.map((img, index) => (
                                    <Image
                                        key={img.asset_image_id || index}
                                        width={100}
                                        height={100}
                                        style={{ objectFit: 'cover', borderRadius: 8 }}
                                        src={img.image_url}
                                    />
                                ))}
                            </Space>
                            <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
                                Upload gambar baru akan mengganti seluruh gambar lama.
                            </div>
                        </Form.Item>
                    )}

                    <Form.Item label="Upload Images">
                        <Upload {...imageUploadProps} listType="picture-card">
                            <div>
                                <UploadOutlined />
                                <div style={{ marginTop: 8 }}>Upload</div>
                            </div>
                        </Upload>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Detail Asset"
                open={isDetailModal}
                onCancel={closeDetailModal}
                footer={[
                    <Button key="close" onClick={closeDetailModal}>
                        Close
                    </Button>,
                ]}
                width={950}
            >
                <Descriptions bordered column={2} size="middle" loading={loadingDetail}>
                    <Descriptions.Item label="Asset Code">{detailData?.asset_code || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Asset Category">
                        {detailData?.category?.name || detailData?.asset_category_id || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Area">
                        {detailData?.area?.name || detailData?.area_id || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Vendor">
                        {detailData?.vendor?.vendor_name || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Asset Name">{detailData?.asset_name || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Serial Number">{detailData?.serial_number || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Brand">{detailData?.brand || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Model">{detailData?.model || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Purchase Date">
                        {detailData?.purchase_date ? dayjs(detailData.purchase_date).format('YYYY-MM-DD') : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Purchase Price">{currencyFormat(detailData?.purchase_price)}</Descriptions.Item>
                    <Descriptions.Item label="Location">{detailData?.location || '-'}</Descriptions.Item>
                    <Descriptions.Item label="User">{detailData?.user?.name || detailData?.users_id || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Position">{detailData?.user?.position?.position_name || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Condition Status">
                        {renderConditionStatus(detailData?.condition_status)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Asset Status">
                        {renderAssetStatus(detailData?.status?.name || getAssetStatusNameById(detailData?.asset_status_id))}
                    </Descriptions.Item>
                    <Descriptions.Item label="Notes" span={2}>
                        {detailData?.notes || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Images" span={2}>
                        {detailData?.images?.length > 0 ? (
                            <Space wrap>
                                {detailData.images.map((img, index) => (
                                    <Image
                                        key={img.asset_image_id || index}
                                        width={120}
                                        height={120}
                                        style={{ objectFit: 'cover', borderRadius: 8 }}
                                        src={img.image_url}
                                    />
                                ))}
                            </Space>
                        ) : (
                            '-'
                        )}
                    </Descriptions.Item>
                </Descriptions>
            </Modal>

            <Modal
                title="Import Asset Excel"
                open={isImportModal}
                onCancel={handleCloseImportModal}
                onOk={handleImportExcel}
                okText="Import"
                confirmLoading={loadingImport}
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
                        Download Template
                    </Button>

                    <Upload {...uploadProps}>
                        <Button icon={<UploadOutlined />}>Pilih File Excel</Button>
                    </Upload>

                    <div style={{ fontSize: 12, color: '#666' }}>
                        Gunakan file template: <b>TEMPLATE_ASSET.xlsx</b> dan upload file format <b>.xlsx</b>.
                    </div>
                </Space>
            </Modal>
        </div>
    );
}

export default Asset;